"use client";

import { forwardRef, useRef, useState, useEffect } from "react";
import { Eye, EyeOff, AlertTriangle } from "lucide-react";
import { Input } from "@/components/ui/input";
import { cn } from "@/lib/utils";
import { hasPhysicalKeyboard } from "@/lib/utils/input-device";

/* Reading the Caps Lock state is best-effort, not a given. `getModifierState` is
   absent on the synthetic key events some embedded WebViews dispatch, and throws
   outright on a few WebKit builds asked about a modifier they do not model. A
   password field must never let that take the form down with it. */
function readCapsLock(e: React.KeyboardEvent<HTMLInputElement>): boolean {
  try {
    return typeof e.getModifierState === "function" && e.getModifierState("CapsLock");
  } catch {
    return false;
  }
}

interface PasswordInputProps {
  value: string;
  onChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
  placeholder?: string;
  autoComplete?: string;
  className?: string;
  disabled?: boolean;
  id?: string;
  onFocus?: () => void;
  "aria-invalid"?: boolean;
  "aria-describedby"?: string;
}

const PasswordInput = forwardRef<HTMLInputElement, PasswordInputProps>(
  (
    {
      value,
      onChange,
      placeholder = "",
      autoComplete = "new-password",
      className,
      disabled,
      id = "password",
      onFocus,
      "aria-invalid": ariaInvalid,
      "aria-describedby": ariaDescribedBy,
    },
    ref
  ) => {
    const [visible, setVisible] = useState(false);
    const [capsLock, setCapsLock] = useState(false);

    /* A software keyboard has no lock to warn about, so the warning is only ever
       raised where a physical keyboard is plausible. Resolved on the first key event
       rather than during render or in an effect: by then we are certainly on the
       client, and the answer cannot change for the life of the field. */
    const physicalKeyboard = useRef<boolean | null>(null);

    function handleKeyEvent(e: React.KeyboardEvent<HTMLInputElement>) {
      if (physicalKeyboard.current === null) {
        physicalKeyboard.current = hasPhysicalKeyboard();
      }
      if (!physicalKeyboard.current) return;

      setCapsLock(readCapsLock(e));
    }

    useEffect(() => {
      if (!value && capsLock) {
        const t = setTimeout(() => setCapsLock(false), 0);
        return () => clearTimeout(t);
      }
    }, [value, capsLock]);

    return (
      <div className="space-y-1">
        <div className="relative">
          <Input
            id={id}
            ref={ref}
            type={visible ? "text" : "password"}
            value={value}
            onChange={onChange}
            onFocus={onFocus}
            onKeyUp={handleKeyEvent}
            onKeyDown={handleKeyEvent}
            placeholder={placeholder}
            autoComplete={autoComplete}
            name="password"
            disabled={disabled}
            aria-invalid={ariaInvalid}
            aria-describedby={ariaDescribedBy}
            className={cn(
              "h-11 global-border-radius pr-10 focus-visible:ring-brand-teal focus-visible:ring-2",
              className
            )}
          />

          <button
            type="button"
            onClick={() => setVisible(!visible)}
            className="absolute -translate-y-1/2 right-3 top-1/2 text-muted-foreground hover:text-foreground"
            tabIndex={-1}
            aria-label={visible ? "Hide password" : "Show password"}
          >
            {visible ? <EyeOff className="w-5 h-5 text-brand-grey" /> : <Eye className="w-5 h-5 text-brand-grey" />}
          </button>
        </div>

        {capsLock && (
          <div className="flex items-center gap-2 text-sm text-brand-warn-strong">
            <AlertTriangle className="w-4 h-4" />
            <span>Caps Lock is on</span>
          </div>
        )}
      </div>
    );
  }
);

PasswordInput.displayName = "PasswordInput";

export default PasswordInput;